/**
 * Marchio dell'azienda che usa il gestionale.
 *
 * Nome, colore e logo arrivano dall'API a ogni avvio, non dal bundle: un
 * cliente cambia il proprio marchio dalla configurazione, senza che nessuno
 * debba ricompilare l'applicazione.
 */

/** Valori usati finché l'API non risponde, o se non risponde affatto. */
export const DEFAULT_BRANDING = Object.freeze({
  name: null,
  subtitle: null,
  brandColor: "#0077a8",
  // Secondo colore e categorie vendute, raccolti dalla configurazione
  // iniziale. Il colore è facoltativo; le categorie a null valgono «non ha
  // ancora risposto», e i Listini restano l'unica scheda generica di prima.
  accentColor: null,
  productCategories: null,
  // Le merceologie che hanno articoli: servono a non far sparire dai listini
  // quello che è stato caricato prima della domanda «cosa vendi».
  itemCategoriesInUse: [],
  hasLogo: false,
  // Cambia a ogni caricamento del logo: entra nell'indirizzo dell'immagine e
  // fa scadere la copia tenuta dal browser.
  logoVersion: null,
  configured: false,
});

/**
 * URL del logo servito dall'API. Non punta al bundle.
 *
 * La rotta ha un indirizzo fisso e risponde con cinque minuti di cache: senza
 * `version` nell'indirizzo, chi carica un logo nuovo continua a vedere il
 * vecchio finché non ricarica la pagina a mano. [sc-48350]
 */
export function companyLogoUrl(
  apiBaseUrl = process.env.VUE_APP_API_URL,
  version = null
) {
  const base = `${String(apiBaseUrl || "").replace(
    /\/$/,
    ""
  )}/company-profile/logo`;
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
}

/**
 * Scrive il colore del marchio nella CSS variable `--brand`, già usata dai
 * fogli di stile: cambiare il colore non richiede altre modifiche al CSS.
 *
 * Il valore è validato anche qui e non solo lato server: quello che finisce in
 * uno stile inline non deve dipendere dalla buona fede di chi risponde.
 */
export function applyBrandColor(color, root = document.documentElement) {
  const value = String(color || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return false;
  root.style.setProperty("--brand", value);
  // Le trasparenze del marchio (aloni di focus, sfondi tenui) non possono
  // partire da un esadecimale: servono le tre componenti separate.
  root.style.setProperty("--brand-rgb", hexToRgbTriplet(value));
  // Sfumatura scura per gli stati attivi (hover, pressione). Nel CSS originale
  // era scritta a mano come #00658f, che è esattamente #0077a8 scurito del
  // 15%: qui si ottiene la stessa tinta partendo da qualsiasi marchio.
  root.style.setProperty("--brand-hover", darkenHex(value));
  // [sc-48583] Con la tavolozza intera il marchio puo' essere chiarissimo, e
  // le scritte sopra i pulsanti sono bianche: qui si decide se restare bianchi
  // o passare al nero. Il CSS legge questa, con `#fff` come ripiego.
  root.style.setProperty("--brand-ink", inkFor(value));
  // E il colore da usare quando il marchio fa da testo invece che da fondo.
  root.style.setProperty("--brand-on-light", readableOnWhite(value));
  return true;
}

/** Scurisce un esadecimale della quota indicata (0-1). */
export function darkenHex(hex, amount = 0.15) {
  const value = String(hex || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  const factor = Math.min(Math.max(1 - amount, 0), 1);
  const n = parseInt(value.slice(1), 16);
  const channel = (shift) =>
    Math.round(((n >> shift) & 255) * factor)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/** `#0077a8` → `"0, 119, 168"`, la forma che serve dentro `rgba()`. */
export function hexToRgbTriplet(hex) {
  const value = String(hex || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  const n = parseInt(value.slice(1), 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/**
 * Le tinte proposte per il marchio e per l'accento.
 *
 * [sc-48583] Prima si sceglieva col selettore del sistema operativo, che
 * chiede tre numeri fra 0 e 255: nessuno sa a memoria che il proprio blu e'
 * 0-119-168, e chi provava usciva con una tinta a caso.
 *
 * La griglia e' quella dei documenti Google, colonne di tinta e righe di
 * sfumatura: dello stesso blu ci sono sette gradi, e un marchio si ritrova
 * per avvicinamento invece che per tentativi. La prima riga sono i grigi.
 *
 * Non c'e' nessun vincolo di chiarezza, e i pastelli servono: il testo che
 * finisce sopra il colore si adatta da se' - vedi `inkFor`. Non e' nemmeno un
 * elenco chiuso: un colore gia' salvato che non sta qui dentro resta valido e
 * la griglia lo mostra lo stesso, in testa.
 */
// Dieci per riga si leggono come la griglia che diventano; una tinta per riga
// sarebbe un elenco di ottanta numeri, ed e' per questo che prettier qui sta
// fuori.
// prettier-ignore
export const BRAND_PALETTE = Object.freeze([
  // grigi
  "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
  // vivi
  "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
  // chiari
  "#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
  "#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
  "#cc4125", "#e06666", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0",
  // scuri
  "#a61c00", "#cc0000", "#e69138", "#f1c232", "#6aa84f", "#45818e", "#3c78d8", "#3d85c6", "#674ea7", "#a64d79",
  "#85200c", "#990000", "#b45f06", "#bf9000", "#38761d", "#134f5c", "#1155cc", "#0b5394", "#351c75", "#741b47",
  "#5b0f00", "#660000", "#783f04", "#7f6000", "#274e13", "#0c343d", "#1c4587", "#073763", "#20124d", "#4c1130",
]);

/**
 * Il nero delle scritte quando il fondo e' troppo chiaro per il bianco.
 *
 * E' nero pieno e non un grigio scurissimo per un motivo misurato: con
 * `#111827` tre tinte della tavolozza - il rosso acceso e due blu di mezzo -
 * restavano sotto il 4,5 a 1 con tutt'e due gli inchiostri. Col nero la
 * peggiore sta a 4,58, e non c'e' nessuna casella che il cliente possa
 * scegliere e poi non riesca a leggere.
 */
export const INK_DARK = "#000000";

/** Quanto e' chiara una tinta, secondo la formula WCAG. */
export function relativeLuminance(hex) {
  const value = String(hex || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  const n = parseInt(value.slice(1), 16);
  const canale = (shift) => {
    const c = ((n >> shift) & 255) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * canale(16) + 0.7152 * canale(8) + 0.0722 * canale(0);
}

/**
 * Di che colore scrivere sopra questa tinta: bianco o quasi nero.
 *
 * [sc-48583] Fino a ieri le scritte sopra il marchio erano bianche e basta, e
 * la tavolozza poteva contenere solo tinte scure. Con la griglia intera un
 * cliente puo' scegliersi un giallo chiaro, e su quello «ACCEDI» in bianco non
 * si legge: qui si sceglie il piu' leggibile dei due, che e' quello che stacca
 * di piu' dal fondo.
 */
/**
 * Lo stesso colore, scurito quanto basta per leggerlo su fondo chiaro.
 *
 * [sc-48583] Il marchio non fa solo da fondo ai pulsanti: in cinquantasei
 * punti fa da colore al testo - collegamenti, titoli, numeri delle schede -
 * e li' sotto c'e' il bianco della pagina. Con la tavolozza intera un giallo
 * chiaro li avrebbe fatti sparire. Si scurisce a passi finche' non stacca
 * abbastanza; se il colore va gia' bene torna se stesso.
 */
export function readableOnWhite(color) {
  const value = String(color || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  let tinta = value.toLowerCase();
  // Venti passi da un decimo bastano ad arrivare al nero da qualsiasi tinta.
  for (let i = 0; i < 20; i += 1) {
    const luminanza = relativeLuminance(tinta);
    if (1.05 / (luminanza + 0.05) >= 4.5) return tinta;
    tinta = darkenHex(tinta, 0.1);
  }
  return tinta;
}

export function inkFor(color) {
  const luminanza = relativeLuminance(color);
  if (luminanza === null) return "#ffffff";
  const colBianco = 1.05 / (luminanza + 0.05);
  const colNero = (luminanza + 0.05) / (relativeLuminance(INK_DARK) + 0.05);
  return colBianco >= colNero ? "#ffffff" : INK_DARK;
}

/**
 * Grigio della piastrella sotto le icone, quando il cliente non sceglie un
 * accento. È il valore che l'interfaccia ha oggi: il ripiego serve a lasciare
 * tutto com'era, non a inventare un colore.
 */
export const DEFAULT_ACCENT = "#444444";

/** Schiarisce un esadecimale della quota indicata (0-1). */
export function lightenHex(hex, amount = 0.1) {
  const value = String(hex || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  const factor = Math.min(Math.max(amount, 0), 1);
  const n = parseInt(value.slice(1), 16);
  const channel = (shift) => {
    const base = (n >> shift) & 255;
    return Math.round(base + (255 - base) * factor)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/**
 * Scrive il secondo colore nelle CSS variable della piastrella sotto le icone.
 *
 * Solo `--accent` e le due tappe del gradiente: niente `--accent-rgb` né
 * `--accent-hover`, che oggi nascerebbero morti perché nessuna trasparenza e
 * nessuno stato attivo usano l'accento.
 *
 * Le due tappe del gradiente sono le stesse che `HomePage.css` dichiara come
 * valori di partenza: qui vengono riscritte a partire dall'accento scelto,
 * così la sfumatura segue il colore invece di restare grigia.
 *
 * @returns {boolean} falso se il colore era malformato e non si è scritto niente
 */
export function applyAccentColor(color, root = document.documentElement) {
  const value = String(color || "").trim() || DEFAULT_ACCENT;
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return false;
  root.style.setProperty("--accent", value);
  root.style.setProperty("--icon-444-a", lightenHex(value));
  root.style.setProperty("--icon-444-b", darkenHex(value));
  // Le icone stanno sopra questa piastrella e sono bianche di partenza: su un
  // accento chiaro sparirebbero. `--icon-fg` e' gia' la variabile che le tinge.
  const inchiostro = inkFor(value);
  root.style.setProperty("--icon-fg", inchiostro);
  // Due delle cinque icone della home sono PNG monocromatiche e non hanno un
  // colore da cambiare: si tingono col filtro. Senza questa riga la piastrella
  // resterebbe con due icone bianche e tre nere.
  root.style.setProperty(
    "--icon-img-filter",
    inchiostro === "#ffffff" ? "brightness(0) invert(1)" : "brightness(0)"
  );
  return true;
}

/**
 * Limite del logo, lo stesso di `LOGO_MAX_BYTES` lato API. [sc-48350]
 * Un mebibyte esatto: è quello che multer confronta, quindi il conto va fatto
 * uguale o il modulo direbbe che va bene un file che il server rifiuta.
 */
export const LOGO_MAX_BYTES = 1024 * 1024;

/** Formati che l'API riconosce dai magic bytes, non solo dall'estensione. */
export const LOGO_MIME_TYPES = Object.freeze([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

/**
 * Perché questo file non può fare da logo, o null se va bene.
 *
 * Sta qui e non nel componente perché il controllo va fatto prima di mandare:
 * un rifiuto del server dopo aver caricato tre mega dice la stessa cosa, ma
 * dopo l'attesa e con parole che non spiegano quanto pesa il file scelto.
 *
 * @param {{ size: number, type: string }|null|undefined} file
 * @returns {string|null}
 */
export function logoRejectionReason(file) {
  if (!file) return null;
  if (!LOGO_MIME_TYPES.includes(file.type)) {
    return "Formato non ammesso: servono PNG, JPEG o WEBP.";
  }
  if (file.size > LOGO_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1).replace(".", ",");
    return `Il logo pesa ${mb} MB: il limite è 1 MB. Ridimensionalo e riprova.`;
  }
  return null;
}
